/**
 * Custom lezer-markdown syntax: [[wikilinks]], #tags, ==highlight==, and
 * Discord-style `-# subtext` lines. Parsing only — rendering/hiding happens in live-preview.ts, and link
 * resolution (does the target exist?) is wired up in the links phase.
 */
import { Tag, styleTags, tags as t } from "@lezer/highlight";
import type { BlockContext, InlineContext, MarkdownConfig } from "@lezer/markdown";

export const customTags = {
  wikilink: Tag.define(),
  wikilinkMark: Tag.define(),
  hashtag: Tag.define(),
  highlight: Tag.define(),
  highlightMark: Tag.define(),
  subtext: Tag.define(),
  subtextMark: Tag.define(),
};

const WIKILINK_OPEN = 91; /* [ */
const HASH = 35; /* # */
const EQ = 61; /* = */
const DASH = 45; /* - */

function isWordChar(ch: number): boolean {
  return (
    (ch >= 48 && ch <= 57) || // 0-9
    (ch >= 65 && ch <= 90) || // A-Z
    (ch >= 97 && ch <= 122) || // a-z
    ch === 45 || // -
    ch === 95 || // _
    ch === 47 || // /
    ch > 127 // unicode
  );
}

export const wikilinkExtension: MarkdownConfig = {
  defineNodes: [
    { name: "Wikilink", style: customTags.wikilink },
    { name: "WikilinkMark", style: customTags.wikilinkMark },
  ],
  parseInline: [
    {
      name: "Wikilink",
      before: "Link",
      parse(cx: InlineContext, next: number, pos: number): number {
        if (next !== WIKILINK_OPEN || cx.char(pos + 1) !== WIKILINK_OPEN) return -1;
        // scan for ]] on the same line, no nested [[
        let end = -1;
        for (let i = pos + 2; i < cx.end - 1; i++) {
          const ch = cx.char(i);
          if (ch === 10) break; // newline
          if (ch === WIKILINK_OPEN && cx.char(i + 1) === WIKILINK_OPEN) break;
          if (ch === 93 /* ] */ && cx.char(i + 1) === 93) {
            end = i + 2;
            break;
          }
        }
        if (end === -1 || end === pos + 4) return -1; // empty [[]]
        return cx.addElement(
          cx.elt("Wikilink", pos, end, [
            cx.elt("WikilinkMark", pos, pos + 2),
            cx.elt("WikilinkMark", end - 2, end),
          ]),
        );
      },
    },
  ],
};

export const hashtagExtension: MarkdownConfig = {
  defineNodes: [{ name: "Hashtag", style: customTags.hashtag }],
  parseInline: [
    {
      name: "Hashtag",
      parse(cx: InlineContext, next: number, pos: number): number {
        if (next !== HASH) return -1;
        // must be at start or preceded by whitespace/punctuation-ish
        if (pos > cx.offset) {
          const prev = cx.char(pos - 1);
          if (isWordChar(prev) || prev === HASH) return -1;
        }
        let i = pos + 1;
        let sawLetter = false;
        while (i < cx.end && isWordChar(cx.char(i))) {
          const ch = cx.char(i);
          if (!(ch >= 48 && ch <= 57)) sawLetter = true;
          i++;
        }
        // tags must contain a non-digit (so "#123" heading refs stay text)
        if (i === pos + 1 || !sawLetter) return -1;
        return cx.addElement(cx.elt("Hashtag", pos, i));
      },
    },
  ],
};

export const highlightExtension: MarkdownConfig = {
  defineNodes: [
    { name: "MarkHighlight", style: customTags.highlight },
    { name: "MarkHighlightMark", style: customTags.highlightMark },
  ],
  parseInline: [
    {
      name: "MarkHighlight",
      before: "Emphasis",
      parse(cx: InlineContext, next: number, pos: number): number {
        if (next !== EQ || cx.char(pos + 1) !== EQ) return -1;
        if (cx.char(pos + 2) === EQ) return -1; // === is not a highlight open
        let end = -1;
        for (let i = pos + 2; i < cx.end - 1; i++) {
          const ch = cx.char(i);
          if (ch === 10) break;
          if (ch === EQ && cx.char(i + 1) === EQ) {
            end = i + 2;
            break;
          }
        }
        if (end === -1 || end === pos + 4) return -1;
        return cx.addElement(
          cx.elt("MarkHighlight", pos, end, [
            cx.elt("MarkHighlightMark", pos, pos + 2),
            cx.elt("MarkHighlightMark", end - 2, end),
          ]),
        );
      },
    },
  ],
};

/** True when the line opens with `-# ` (the space is required, as in Discord). */
function isSubtextLine(text: string, pos: number): boolean {
  if (text.charCodeAt(pos) !== DASH || text.charCodeAt(pos + 1) !== HASH) return false;
  const after = text.charCodeAt(pos + 2);
  return after === 32 || after === 9;
}

/**
 * `-# text` is a single-line leaf block like an ATX heading, rendered small
 * and dimmed. It interrupts a paragraph the way `#` does, so a run of
 * subtext lines stays one block per line.
 */
export const subtextExtension: MarkdownConfig = {
  defineNodes: [
    { name: "Subtext", block: true, style: customTags.subtext },
    { name: "SubtextMark", style: customTags.subtextMark },
  ],
  parseBlock: [
    {
      name: "Subtext",
      before: "BulletList",
      parse(cx, line) {
        if (line.indent - line.baseIndent >= 4 || !isSubtextLine(line.text, line.pos)) return false;
        const from = cx.lineStart + line.pos;
        const to = cx.lineStart + line.text.length;
        const contentStart = line.pos + 3;
        const children = [
          cx.elt("SubtextMark", from, from + 2),
          ...cx.parser.parseInline(line.text.slice(contentStart), cx.lineStart + contentStart),
        ];
        cx.nextLine();
        cx.addElement(cx.elt("Subtext", from, to, children));
        return true;
      },
      endLeaf: (_cx, line) => line.indent - line.baseIndent < 4 && isSubtextLine(line.text, line.pos),
    },
  ],
};

/** Style mappings for the standard markdown nodes we care about. */
export const markdownStyles: MarkdownConfig = {
  props: [
    styleTags({
      "Wikilink/...": customTags.wikilink,
      Hashtag: customTags.hashtag,
      "MarkHighlight/...": customTags.highlight,
      SubtextMark: customTags.subtextMark,
    }),
  ],
};

export const hydraMarkdownExtensions = [wikilinkExtension, hashtagExtension, highlightExtension, subtextExtension];

// Re-export for consumers that need the node names.
export type { BlockContext };
